import Num from '@flipster/utils/num';

import { formatModalSecondaryDisplay } from '../tpsl/tpsl-basis-field/placeholders';
import { calcFromEntryPercentage, calcOrderEstUnrealizedPnl } from './tp-sl-preview';

export type InlineBasisMode = 'price' | 'roi' | 'pnl';
export type ModalBasisMode = 'change' | 'roi' | 'pnl';

export type TpslBasisContext = {
  side: 'Long' | 'Short';
  entryRefPrice: string;
  qty: string;
  margin: string;
  /** 'order' → order-style helpers; 'position' → position helpers */
  formulaProfile: 'order' | 'position';
  /** order profile: used with leverage when matching calcOrderEstUnrealizedPnl */
  amount?: string;
  leverage?: number;
};

export type PriceBasisState = {
  triggerPrice: string;
  limitPrice: string;
  secondaryValue: string;
  basisMode: ModalBasisMode;
  isLimitFixed: boolean;
};

/** Mode switch / empty reset for modal secondary (placeholder shows sign hint). */
const secondaryResetValue = '';

function isPositiveNumber(value: string | undefined): value is string {
  if (value == null || value === '') return false;
  const n = Num(value);
  return n.isFinite() && n.isGreaterThan(0);
}

function isFiniteNumber(value: string | undefined): value is string {
  if (value == null || value === '') return false;
  const n = Num(value);
  return n.isFinite();
}

function hasNonZero(value: string | undefined): value is string {
  if (!isFiniteNumber(value)) return false;
  return !Num(value).isZero();
}

/** Change% → absolute price. Inverse of calcFromEntryPercentage. */
export function changePercentToPrice(changePercent: string, entryRefPrice: string): string {
  if (!isPositiveNumber(entryRefPrice) || !isFiniteNumber(changePercent)) return '';
  return Num(entryRefPrice)
    .multipliedBy(Num(1).plus(Num(changePercent).dividedBy(100)))
    .toString();
}

/** Absolute price → Change%. Delegates to calcFromEntryPercentage. */
export function priceToChangePercent(price: string, entryRefPrice: string): string {
  return calcFromEntryPercentage(price, entryRefPrice);
}

function resolveOrderMargin(ctx: TpslBasisContext): string | null {
  if (isPositiveNumber(ctx.margin)) return ctx.margin;
  if (isPositiveNumber(ctx.amount) && ctx.leverage != null && ctx.leverage > 0) {
    return Num(ctx.amount).dividedBy(ctx.leverage).toString();
  }
  return null;
}

function canCalculate(ctx: TpslBasisContext): boolean {
  if (!isPositiveNumber(ctx.entryRefPrice)) return false;
  if (ctx.formulaProfile === 'position') {
    return hasNonZero(ctx.qty) && isPositiveNumber(ctx.margin);
  }
  // order: qty+margin (tpslEstPnlAtom) or amount+leverage (calcOrderEstUnrealizedPnl)
  if (hasNonZero(ctx.qty) && resolveOrderMargin(ctx) != null) return true;
  return isPositiveNumber(ctx.amount) && ctx.leverage != null && ctx.leverage > 0;
}

function pnlToPriceOrder(pnl: string, ctx: TpslBasisContext): string | null {
  if (!canCalculate(ctx) || !isFiniteNumber(pnl)) return null;

  // Prefer amount/leverage path when both are present (matches calcOrderEstUnrealizedPnl).
  if (isPositiveNumber(ctx.amount) && ctx.leverage != null && ctx.leverage > 0) {
    const amount = Num(ctx.amount);
    if (amount.isZero()) return null;
    const ratio = Num(pnl).dividedBy(amount);
    // Long: exit = entry * (1 + pnl/amount); Short: exit = entry * (1 - pnl/amount)
    const factor = ctx.side === 'Long' ? Num(1).plus(ratio) : Num(1).minus(ratio);
    return Num(ctx.entryRefPrice).multipliedBy(factor).toString();
  }

  const qty = Num(ctx.qty);
  if (qty.isZero()) return null;
  // Long: exit = entry + pnl/qty; Short: exit = entry - pnl/qty
  const delta = Num(pnl).dividedBy(qty);
  return ctx.side === 'Long'
    ? Num(ctx.entryRefPrice).plus(delta).toString()
    : Num(ctx.entryRefPrice).minus(delta).toString();
}

function pnlToPricePosition(pnl: string, ctx: TpslBasisContext): string | null {
  if (!canCalculate(ctx) || !isFiniteNumber(pnl)) return null;
  const qty = Num(ctx.qty);
  if (qty.isZero()) return null;
  // position.position is signed; PnL = (exit - entry) * qty → exit = entry + PnL/qty
  return Num(ctx.entryRefPrice).plus(Num(pnl).dividedBy(qty)).toString();
}

function roiToPnl(roiPercent: string, margin: string): string | null {
  if (!isFiniteNumber(roiPercent) || !isPositiveNumber(margin)) return null;
  return Num(roiPercent).multipliedBy(margin).dividedBy(100).toString();
}

function forwardOrder(targetPrice: string, ctx: TpslBasisContext): { pnl: string; roi: string } | null {
  if (!canCalculate(ctx) || !isPositiveNumber(targetPrice)) return null;

  if (isPositiveNumber(ctx.amount) && ctx.leverage != null && ctx.leverage > 0) {
    const result = calcOrderEstUnrealizedPnl(ctx.side, ctx.entryRefPrice, targetPrice, ctx.amount, ctx.leverage);
    return { pnl: result.estUnrealizedPnl, roi: result.estUnrealizedPnlPct };
  }

  const margin = resolveOrderMargin(ctx);
  if (margin == null || !hasNonZero(ctx.qty)) return null;
  const qty = Num(ctx.qty);
  const pnl =
    ctx.side === 'Long'
      ? Num(targetPrice).minus(ctx.entryRefPrice).multipliedBy(qty)
      : Num(ctx.entryRefPrice).minus(targetPrice).multipliedBy(qty);
  const roi = pnl.dividedBy(margin).multipliedBy(100);
  return { pnl: pnl.toString(), roi: roi.toString() };
}

function forwardPosition(targetPrice: string, ctx: TpslBasisContext): { pnl: string; roi: string } | null {
  if (!canCalculate(ctx) || !isPositiveNumber(targetPrice)) return null;
  // Same formula as calcPositionEstUnrealizedPnl (qty is signed for Short).
  const pnl = Num(targetPrice).minus(ctx.entryRefPrice).multipliedBy(ctx.qty);
  const roi = pnl.dividedBy(ctx.margin).multipliedBy(100);
  return { pnl: pnl.toString(), roi: roi.toString() };
}

/**
 * Secondary basis value → absolute target price.
 * Returns null when inputs are insufficient / invalid.
 */
export function basisValueToTargetPrice(
  mode: Exclude<ModalBasisMode | InlineBasisMode, 'price'>,
  value: string,
  ctx: TpslBasisContext,
): string | null {
  if (!isFiniteNumber(value)) return null;

  if (mode === 'change') {
    if (!isPositiveNumber(ctx.entryRefPrice)) return null;
    const price = changePercentToPrice(value, ctx.entryRefPrice);
    return isPositiveNumber(price) ? price : null;
  }

  if (!canCalculate(ctx)) return null;

  let pnl: string | null = value;
  if (mode === 'roi') {
    const margin = ctx.formulaProfile === 'order' ? resolveOrderMargin(ctx) : ctx.margin;
    if (margin == null) return null;
    pnl = roiToPnl(value, margin);
  }
  if (pnl == null) return null;

  return ctx.formulaProfile === 'position' ? pnlToPricePosition(pnl, ctx) : pnlToPriceOrder(pnl, ctx);
}

/**
 * Absolute target price → secondary basis value.
 * Returns null when inputs are insufficient / invalid.
 */
export function targetPriceToBasisValue(
  mode: Exclude<ModalBasisMode | InlineBasisMode, 'price'>,
  targetPrice: string,
  ctx: TpslBasisContext,
): string | null {
  if (!isPositiveNumber(targetPrice)) return null;

  if (mode === 'change') {
    if (!isPositiveNumber(ctx.entryRefPrice)) return null;
    return priceToChangePercent(targetPrice, ctx.entryRefPrice);
  }

  if (!canCalculate(ctx)) return null;

  const forward =
    ctx.formulaProfile === 'position' ? forwardPosition(targetPrice, ctx) : forwardOrder(targetPrice, ctx);
  if (!forward) return null;
  return mode === 'pnl' ? forward.pnl : forward.roi;
}

/**
 * Apply secondary-derived absolute to one price field (dual-field independent).
 * - `isLimitFixed` true → Limit row secondary → `limitPrice` only
 * - false → Trigger row secondary → `triggerPrice` only
 * (Do not equalize trigger↔limit — each row owns its price.)
 */
function applyTargetPrice(state: PriceBasisState, targetPrice: string): PriceBasisState {
  if (state.isLimitFixed) {
    return { ...state, limitPrice: targetPrice };
  }
  return { ...state, triggerPrice: targetPrice };
}

/** Secondary (Change/ROI/PnL) edit → update that row's absolute price only. */
export function syncOnSecondaryChange(
  state: PriceBasisState,
  nextSecondary: string,
  ctx: TpslBasisContext,
): PriceBasisState {
  const next: PriceBasisState = { ...state, secondaryValue: nextSecondary };
  const targetPrice = basisValueToTargetPrice(state.basisMode, nextSecondary, ctx);
  if (targetPrice == null) return next;
  return applyTargetPrice(next, targetPrice);
}

/**
 * Trigger price edit ↔ secondary forward sync.
 *
 * When `isLimitFixed` is true, `secondaryValue` is treated as the **Limit/target**
 * secondary (Est basis) — update trigger only, leave secondary unchanged.
 * Call sites for the **Trigger row** dual-field must pass `isLimitFixed: false` so
 * Trigger Change/ROI/PnL always tracks trigger price (even when Limit Order is on).
 */
export function syncOnTriggerPriceChange(
  state: PriceBasisState,
  nextTrigger: string,
  ctx: TpslBasisContext,
): PriceBasisState {
  if (state.isLimitFixed) {
    return { ...state, triggerPrice: nextTrigger };
  }
  if (nextTrigger === '') {
    return { ...state, triggerPrice: '', secondaryValue: '' };
  }
  const secondaryValue = targetPriceToBasisValue(state.basisMode, nextTrigger, ctx);
  return {
    ...state,
    triggerPrice: nextTrigger,
    secondaryValue: secondaryValue == null ? state.secondaryValue : formatModalSecondaryDisplay(secondaryValue),
  };
}

/** Limit price edit → recompute secondary from limit (targetPrice). */
export function syncOnLimitPriceChange(
  state: PriceBasisState,
  nextLimit: string,
  ctx: TpslBasisContext,
): PriceBasisState {
  if (nextLimit === '') {
    return { ...state, limitPrice: '', secondaryValue: '' };
  }
  const secondaryValue = targetPriceToBasisValue(state.basisMode, nextLimit, ctx);
  return {
    ...state,
    limitPrice: nextLimit,
    secondaryValue: secondaryValue == null ? state.secondaryValue : formatModalSecondaryDisplay(secondaryValue),
  };
}

/**
 * Whether the metric value field has user input (incl. `0` / `0.00`).
 * Empty / whitespace-only → false (basis-switch keeps empty, no re-prefill).
 */
export function hasBasisSwitchValue(secondaryValue: string): boolean {
  return secondaryValue.trim() !== '';
}

/** Inline Variant A: price mode uses absolute; ROI/PnL uses secondary (absolute as 0% fallback). */
export function hasInlineBasisSwitchValue(params: {
  mode: InlineBasisMode;
  absolute: string | undefined;
  secondary: string;
}): boolean {
  if (hasBasisSwitchValue(params.secondary)) return true;
  return params.absolute != null && params.absolute !== '';
}

export type BasisModeChangeOptions = {
  /**
   * Context default for Trigger price when resetting a non-empty value.
   * Position/Pending → entry/order price; Advanced → `''`.
   */
  triggerPriceDefault: string;
};

/**
 * Basis-switch reset (master §4.4):
 * - empty secondary → mode only; keep empty; do not touch Trigger (no re-prefill)
 * - has secondary value (incl. 0) → Trigger → `triggerPriceDefault`, secondary → `''`
 * - Limit price / Limit Order are never modified here
 */
export function onBasisModeChange(
  state: PriceBasisState,
  nextMode: ModalBasisMode,
  options: BasisModeChangeOptions,
): PriceBasisState {
  if (!hasBasisSwitchValue(state.secondaryValue)) {
    return {
      ...state,
      basisMode: nextMode,
      secondaryValue: secondaryResetValue,
    };
  }
  return {
    ...state,
    basisMode: nextMode,
    triggerPrice: options.triggerPriceDefault,
    secondaryValue: secondaryResetValue,
  };
}
